import { useTranslation } from 'react-i18next';
import { NavLink, Outlet, useLocation } from 'react-router-dom';

import { cn } from '@lemoment/lib/utils';
import { motion } from 'framer-motion';
import { ClipboardList, Globe, Home, Moon, Settings, Sun } from 'lucide-react';

import { Wordmark } from '../components/Wordmark';
import { useThemeStore } from '../core/themeStore';
import { useKeyboardLayout } from '../hooks/useKeyboardLayout';

const TABS = [
  { path: '/', icon: Home, labelKey: 'tabs.home' },
  { path: '/my', icon: ClipboardList, labelKey: 'tabs.myLeaves' },
  { path: '/settings', icon: Settings, labelKey: 'tabs.settings' },
] as const;

const DesktopSidebar = () => {
  const { t, i18n } = useTranslation();
  const { theme, toggleTheme } = useThemeStore();
  const isDark = theme === 'dark';

  return (
    <aside className="fixed left-0 top-0 z-40 hidden h-full w-64 flex-col border-r border-border bg-sidebar/60 backdrop-blur-xl md:flex">
      <div className="flex h-20 items-center px-7">
        <Wordmark className="text-2xl" />
      </div>

      <nav className="flex-1 space-y-1 px-4 pb-2 pt-1">
        {TABS.map(({ path, icon: Icon, labelKey }) => (
          <NavLink key={path} to={path} end={path === '/'}>
            {({ isActive }) => (
              <span
                className={cn(
                  'relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-[0.95rem] transition-all duration-200',
                  isActive
                    ? 'bg-lemon-soft/60 font-semibold text-foreground'
                    : 'font-medium text-muted-foreground hover:bg-accent/60 hover:text-foreground',
                )}
              >
                {isActive && (
                  <motion.span
                    layoutId="nav-bar"
                    className="absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-full bg-lemon"
                  />
                )}
                <Icon size={19} strokeWidth={isActive ? 2.2 : 1.6} />
                <span>{t(labelKey)}</span>
              </span>
            )}
          </NavLink>
        ))}
      </nav>

      <div className="space-y-1 border-t border-border/70 px-4 py-5">
        <button
          onClick={toggleTheme}
          className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-muted-foreground transition-all hover:bg-accent/60 hover:text-foreground"
        >
          {isDark ? (
            <Moon size={18} strokeWidth={1.6} />
          ) : (
            <Sun size={18} strokeWidth={1.6} />
          )}
          <span>
            {isDark ? t('settings.themeDark') : t('settings.themeLight')}
          </span>
        </button>
        <button
          onClick={() =>
            i18n.changeLanguage(i18n.language === 'ko' ? 'en' : 'ko')
          }
          className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-muted-foreground transition-all hover:bg-accent/60 hover:text-foreground"
        >
          <Globe size={18} strokeWidth={1.6} />
          <span>{i18n.language === 'ko' ? '한국어' : 'English'}</span>
        </button>
      </div>
    </aside>
  );
};

const HEADER_CONFIG: Record<string, { title: string; isLogo?: boolean }> = {
  '/': { title: 'Lemoment', isLogo: true },
  '/my': { title: 'tabs.myLeaves' },
  '/settings': { title: 'tabs.settings' },
};

const MobileHeader = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const config = HEADER_CONFIG[pathname] ?? HEADER_CONFIG['/'];

  return (
    <header className="fixed left-0 right-0 top-0 z-40 safe-top md:hidden">
      <div className="glass-surface flex h-14 items-center justify-between border-b border-border/60 px-5">
        {config.isLogo ? (
          <Wordmark className="text-xl" />
        ) : (
          <span className="font-display text-lg font-semibold tracking-tight">
            {t(config.title)}
          </span>
        )}
        <span className="eyebrow text-muted-foreground/80">근태</span>
      </div>
    </header>
  );
};

const MobileTabBar = () => {
  const { t } = useTranslation();
  const { visible: isKeyboardVisible } = useKeyboardLayout();

  if (isKeyboardVisible) {
    return null;
  }

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 safe-bottom px-4 pb-2 md:hidden">
      <div className="glass-surface mx-auto flex max-w-md items-center justify-around rounded-2xl border border-border/70 px-2 py-1.5">
        {TABS.map(({ path, icon: Icon, labelKey }) => (
          <NavLink
            key={path}
            to={path}
            end={path === '/'}
            className="relative flex flex-col items-center"
          >
            {({ isActive }) => (
              <div
                className={cn(
                  'relative flex flex-col items-center gap-1 rounded-xl px-5 py-1.5 transition-colors',
                  isActive ? 'text-foreground' : 'text-muted-foreground',
                )}
              >
                {isActive && (
                  <motion.div
                    layoutId="tab-pill"
                    className="absolute inset-0 rounded-xl bg-lemon-soft/70"
                    transition={{
                      type: 'spring' as const,
                      stiffness: 500,
                      damping: 35,
                    }}
                  />
                )}
                <Icon
                  size={20}
                  strokeWidth={isActive ? 2.2 : 1.6}
                  className="relative z-10"
                />
                <span
                  className={cn(
                    'relative z-10 text-[10px] leading-tight',
                    isActive ? 'font-semibold' : 'font-medium',
                  )}
                >
                  {t(labelKey)}
                </span>
              </div>
            )}
          </NavLink>
        ))}
      </div>
    </nav>
  );
};

export const ResponsiveLayout = () => {
  return (
    <>
      <DesktopSidebar />
      <MobileHeader />
      <main className="min-h-[100dvh] safe-top md:ml-64">
        <Outlet />
      </main>
      <MobileTabBar />
    </>
  );
};
